/* Ayudate · Registro de tutor */

import React, { useState, useEffect } from "react";
import { BuscadorCursos } from "./BuscadorCursos";
import { Grilla } from "./Grilla";
import { AvisoPoliticas } from "./Politicas";
import { ListaErrores, Ayuda, NotaCorreo, AlertaArchivo, DisponibilidadUsuario } from "./Mensajes";
import { TelefonoInput } from "./TelefonoInput";
import { CampoPassword } from "./CampoPassword";
import { infoCurso, INSTITUCIONES } from "../datos/areas";
import { colones } from "../lib/formato";
import { precioDe, TARIFA_MINIMA, TARIFA_SUGERIDA } from "../lib/tarifas";
import { ANIOS_TUTOR, escalaDeCurso, esDeOpciones, fueraDeEscala, pideRespaldo,
         soloDigitos, valorDeEscalaOk } from "../lib/horarios";
import { prepararFoto } from "../lib/foto";
import { prepararComprobante } from "../lib/comprobante";
import { validarExtensionImagen, FORMATOS_IMAGEN_TEXTO } from "../lib/tiposArchivo";
import { vNombreCompleto, vCorreo, vTelefono, vCarrera, vAnio, vUsuario, vPassword, sanitizarTexto }
  from "../lib/validar";
import { useDisponibilidadUsuario } from "../lib/disponibilidadUsuario";
import { irA_ruta } from "../lib/rutas";
import { CODIGO_PAIS_PREDETERMINADO } from "../datos/codigosPais";
import { TIPOS_CUENTA } from "../datos/cuentasBancarias";
import { api, espera } from "../api";

export function FormTutor() {
  const [nombre, setNombre] = useState("");
  const [correo, setCorreo] = useState("");
  const [carrera, setCarrera] = useState("");
  const [anio, setAnio] = useState("");
  const [codigoTel, setCodigoTel] = useState(CODIGO_PAIS_PREDETERMINADO);
  const [telefono, setTelefono] = useState("");
  const [cuentaTipo, setCuentaTipo] = useState("");       // banco/tipo de cuenta para sus pagos
  const [cuentaNumero, setCuentaNumero] = useState("");    // número de cuenta o IBAN
  const [cuentaTitular, setCuentaTitular] = useState("");  // a nombre de quién está la cuenta
  const [usuario, setUsuario] = useState("");   // credenciales del dashboard, elegidas acá mismo
  const [password, setPassword] = useState("");
  const [confirmarPassword, setConfirmarPassword] = useState("");
  const disponibilidadUsuario = useDisponibilidadUsuario(usuario);
  const [categorias, setCategorias] = useState([]);   // instituciones donde da tutorías: tec/idiomas/colegios/examadm, varias a la vez
  const [cursosSel, setCursosSel] = useState([]);   // cursos elegidos
  const [notas, setNotas] = useState({});           // nota obtenida por curso
  const [precios, setPrecios] = useState({});   // tarifa por curso
  const [descs, setDescs] = useState({});   // descripción por curso
  const [comprobantes, setComprobantes] = useState({});   // comprobante por curso, ya preparado
  const [preparandoComp, setPreparandoComp] = useState({});   // curso -> boolean, mientras procesa
  const [disp, setDisp] = useState({});
  const [error, setError] = useState("");
  const [intento, setIntento] = useState(false);   // ya intentó enviar
  const [enviando, setEnviando] = useState(false);
  const [progreso, setProgreso] = useState(0);      // % de la barra de carga, ver el efecto más abajo
  const [foto, setFoto] = useState(null);          // vista previa ya reducida
  const [errorFoto, setErrorFoto] = useState("");   // archivo de foto rechazado, se muestra junto al campo
  const [errorComp, setErrorComp] = useState({});   // curso -> archivo de comprobante rechazado
  const [preparando, setPreparando] = useState(false);
  const [listo, setListo] = useState(false);
  const [aviso, setAviso] = useState(false);
  const cajaRef = React.useRef(null);
  /* Cada selección de archivo (foto o comprobante) suma su propio contador:
     si el usuario elige un archivo, se arrepiente (Quitar) o elige otro
     antes de que termine de prepararse el primero, la preparación vieja ya
     no coincide con el contador al resolver y se descarta en vez de
     pisar el estado actual con datos de un archivo que ya no está — sin
     esto, un reintento después de un envío fallido podía terminar
     mandando una mezcla de la foto vieja con el estado nuevo. */
  const fotoTokenRef = React.useRef(0);
  const compTokensRef = React.useRef({});

  const marcados = Object.keys(disp).length;
  const sinPrecio = cursosSel.filter((c) => Number(precios[c]) < TARIFA_MINIMA || !precios[c]);
  /* Qué áreas puede elegir el buscador de cursos: la unión de las áreas de
     todas las instituciones marcadas arriba (puede haber varias a la vez).
     Sin ninguna institución elegida todavía, el buscador queda deshabilitado. */
  const areasCategoria = categorias.length > 0
    ? categorias.flatMap((id) => INSTITUCIONES.find((i) => i.id === id)?.areas || [])
    : null;
  /* Cada curso pide lo suyo, y lo pide su ESCALA (ver ESCALAS_NIVEL en
     lib/horarios.js): un idioma su nivel del Marco Común, una herramienta su
     nivel de conocimiento, un curso de universidad su nota de expediente, y
     una materia de admisión la nota que la persona sacó en el examen; una
     materia de colegio no pide nada. No depende de qué botón esté marcado
     (puede haber varios a la vez) sino de la institución del área real del
     curso, así que todas pueden convivir en la misma solicitud, cada una con
     lo que le toca. */
  const escalaDe = (c) => escalaDeCurso(c);
  const pideNivel = (c) => esDeOpciones(escalaDe(c));
  /* Colegios no pide respaldo: ni nota ni comprobante (ver la escala
     "colegio" en lib/horarios.js). Los dos campos desaparecen de su tarjeta
     y no cuentan como faltantes — no se esconden y se validan igual, que es
     como se llega a un formulario que no se deja enviar sin decir por qué. */
  const pideRespaldoDe = (c) => pideRespaldo(escalaDe(c));
  const sinDesc = cursosSel.filter((c) => (descs[c] || "").trim().length < 20);

  /* Cursos a los que todavía les falta el dato, sea un nivel de una lista o
     un número dentro del rango de su escala. */
  const sinNota = cursosSel.filter((c) => !valorDeEscalaOk(escalaDe(c), notas[c]));
  const sinComprobante = cursosSel.filter((c) => pideRespaldoDe(c) && !comprobantes[c]);

  /* Número escrito pero fuera del rango de su escala (80–100 en cursos,
     0–900 en admisión): se avisa al toque, igual que un carácter especial
     inválido, sin esperar al intento de envío. Escribir algo que no sea un
     dígito ya no llega hasta acá: el campo no lo acepta. */
  const notaFueraDeRango = (c) => fueraDeEscala(escalaDe(c), notas[c]);
  const notaFueraViva = cursosSel.some(notaFueraDeRango);
  /* El aviso nombra la escala del curso que está mal, no un rango fijo: con
     un curso del Tec y uno de admisión juntos, "entre 80 y 100" sería
     mentira para uno de los dos. */
  const avisoFueraDeRango = escalaDe(cursosSel.find(notaFueraDeRango) || "")?.fuera || "";

  /* Precio escrito pero por debajo del mínimo: se avisa al toque, igual
     que la nota fuera de rango, sin esperar al intento de envío. */
  const precioBajo = (c) => {
    const v = precios[c];
    if (v === undefined || v === "") return false;
    const n = Number(v);
    return !Number.isNaN(n) && n < TARIFA_MINIMA;
  };

  /* Cada campo devuelve su propio mensaje. Vacío = está bien. */
  const msg = {
    nombre: vNombreCompleto(nombre),
    correo: vCorreo(correo),
    telefono: vTelefono(telefono),
    carrera: vCarrera(carrera),
    anio: vAnio(anio),
    cuentaTipo: cuentaTipo ? "" : "Elija el tipo de cuenta donde le depositamos.",
    cuentaNumero: cuentaNumero.trim() ? "" : "Ingrese el número de cuenta o IBAN.",
    cuentaTitular: cuentaTitular.trim() ? "" : "Ingrese a nombre de quién está la cuenta.",
    usuario: vUsuario(usuario) || (disponibilidadUsuario === "ocupado" ? "Ese usuario ya existe." : ""),
    password: vPassword(password),
    confirmarPassword: password !== confirmarPassword ? "Las contraseñas no coinciden." : "",
    categoria: categorias.length > 0 ? "" : "Elija arriba dónde quiere dar tutorías. Puede elegir varias.",
    cursos: cursosSel.length === 0
      ? "Elija al menos un curso o idioma de la lista."
      : "",
    /* Cada escala dice cómo se llama su dato cuando falta ("el nivel", "la
       nota (un número entre 80 y 100)", "la nota de admisión…"). Si los que
       faltan son de escalas distintas, se cae al genérico: enumerar cuatro
       nombres en una sola línea se lee peor que "el dato". */
    notas: cursosSel.length === 0 ? ""
      : notaFueraViva ? avisoFueraDeRango
      : sinNota.length === 0 ? ""
      : `Falta ${sinNota.every((c) => escalaDe(c).falta === escalaDe(sinNota[0]).falta)
          ? escalaDe(sinNota[0]).falta : "el dato"} de: `
        + sinNota.map((c) => infoCurso(c).nom).join(", ") + ".",
    comprobantes: cursosSel.length === 0 ? ""
      : sinComprobante.length === 0 ? ""
      : `Suba el comprobante de: ` + sinComprobante.map((c) => infoCurso(c).nom).join(", ") + ".",
    descripciones: cursosSel.length === 0 ? ""
      : sinDesc.length === 0 ? ""
      : `Escriba al menos 20 caracteres describiendo: `
        + sinDesc.map((c) => infoCurso(c).nom).join(", ") + ".",
    tarifa: cursosSel.length === 0 ? ""
      : sinPrecio.length === 0 ? ""
      : sinPrecio.length === cursosSel.length
        ? `Ponga cuánto cobra por hora en cada curso. Mínimo ${colones(TARIFA_MINIMA)}.`
        : `Falta el precio de: ${sinPrecio.map((c) => infoCurso(c).sinCodigo ? infoCurso(c).nom : c).join(", ")}.`,
    disponibilidad: marcados === 0
      ? "Marque al menos un bloque en la grilla de la derecha." : "",
  };
  const falta = Object.fromEntries(Object.entries(msg).map(([k, v]) => [k, !!v]));
  const completo = !Object.values(falta).some(Boolean);
  /* Caracteres no permitidos se avisan al toque, sin esperar al intento de envío. */
  const vivo = {
    nombre: /[^A-Za-zÀ-ÿ\s']/.test(nombre),
    carrera: /[^A-Za-zÀ-ÿ\s'-]/.test(carrera),
    telefono: /\D/.test(telefono),
    notas: notaFueraViva,
  };
  const cl = (k) => (vivo[k] || (intento && falta[k]) ? "falta" : "");
  const ay = (k) => ({ error: msg[k], mostrar: vivo[k] || intento });
  /* La lista se recalcula sola: va desapareciendo conforme se completan campos */
  const pendientes = intento ? Object.values(msg).filter(Boolean) : [];

  /* Si hay algo escrito, el navegador pregunta antes de cerrar la pestaña */
  const hayDatos = nombre.trim() || correo.trim() || telefono.trim() || carrera.trim()
    || anio.trim() || categorias.length > 0 || cursosSel.length > 0 || Object.keys(descs).length > 0
    || Object.keys(disp).length > 0 || cuentaTipo || cuentaNumero.trim() || cuentaTitular.trim();
  useEffect(() => {
    if (!hayDatos || listo) return;
    const avisar = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [hayDatos, listo]);

  /* Un solo ciclo de la barra, sin loop: mientras dura el envío (que sube
     imágenes en base64 en el mismo POST, sin forma de medir el avance real
     con fetch) se acerca de a poco a un techo del 90% sin llegar nunca del
     todo — así nunca se ve "llena" mientras todavía se está esperando al
     servidor. El salto real a 100% lo dispara confirmar() en el instante
     exacto en que la respuesta llega, no este efecto. */
  useEffect(() => {
    if (!enviando) return;
    const id = setInterval(() => {
      setProgreso((p) => (p >= 90 ? p : p + (90 - p) * 0.12));
    }, 200);
    return () => clearInterval(id);
  }, [enviando]);

  /* Quita por completo una oferta ya agregada (curso o idioma), junto con
     todo su estado propio — si no se limpiara, un curso distinto elegido
     después con el mismo código heredaría la nota, el precio o el
     comprobante del que se quitó. */
  /* Prender/apagar una institución (TEC/Idiomas/Colegios/Admisión): se puede
     tener varias marcadas a la vez, así que prender una nunca toca lo ya
     elegido. Apagar una sí puede: cualquier oferta ya agregada cuya área
     pertenecía solo a esa institución deja de encajar con el filtro del
     buscador (areasCategoria), así que se quita junto con su nota, precio,
     comprobante y descripción — igual que quitarCurso de un botón "×". */
  const alternarCategoria = (id) => {
    setCategorias((prev) => {
      const activa = prev.includes(id);
      const siguiente = activa ? prev.filter((x) => x !== id) : [...prev, id];
      if (activa) {
        const areasRestantes = new Set(
          siguiente.flatMap((cid) => INSTITUCIONES.find((i) => i.id === cid)?.areas || [])
        );
        cursosSel
          .filter((c) => !areasRestantes.has(infoCurso(c).area))
          .forEach(quitarCurso);
      }
      return siguiente;
    });
  };

  const quitarCurso = (c) => {
    setCursosSel((prev) => prev.filter((x) => x !== c));
    setNotas((prev) => { const p = { ...prev }; delete p[c]; return p; });
    setPrecios((prev) => { const p = { ...prev }; delete p[c]; return p; });
    setDescs((prev) => { const p = { ...prev }; delete p[c]; return p; });
    setComprobantes((prev) => { const p = { ...prev }; delete p[c]; return p; });
    setPreparandoComp((prev) => { const p = { ...prev }; delete p[c]; return p; });
    setErrorComp((prev) => { const p = { ...prev }; delete p[c]; return p; });
    delete compTokensRef.current[c];
  };

  const enviar = () => {
    if (!completo) {
      setIntento(true);
      setTimeout(() => {
        const el = cajaRef.current?.querySelector(".falta");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        if (el?.focus) el.focus({ preventScroll: true });
      }, 40);
      return;
    }
    setError("");
    setAviso(true);
  };

  const confirmar = async () => {
    setAviso(false);
    setEnviando(true); setError(""); setProgreso(0);
    try {
      await api.crearTutor({
        nombre: sanitizarTexto(nombre), correo: sanitizarTexto(correo),
        carrera: sanitizarTexto(carrera), anio, telefono: `${codigoTel} ${telefono}`,
        disponibilidad: disp,
        cuentaTipo,
        cuentaNumero: sanitizarTexto(cuentaNumero),
        cuentaTitular: sanitizarTexto(cuentaTitular),
        usuario: sanitizarTexto(usuario),
        password,
        cursos: cursosSel.map(sanitizarTexto),
        notas,
        comprobantes,
        descripciones: Object.fromEntries(
          Object.entries(descs).map(([c, d]) => [c, sanitizarTexto(d)])
        ),
        foto,
        tarifasPorCurso: Object.fromEntries(cursosSel.map((c) => [c, Number(precios[c])])),
      });
      /* La barra llega a 100% en el instante exacto en que el servidor
         confirma el envío — no antes (mientras "enviando" sigue en true, el
         efecto de arriba la frena en 90%) ni después. Se deja ver ese
         instante un momento antes de pasar a la pantalla de éxito, en vez
         de saltar directo de la barra a "listo" en el mismo render. */
      setProgreso(100);
      await espera(350);
      setListo(true);
      setEnviando(false);
    } catch (err) {
      /* err.message trae el motivo puntual cuando el servidor lo manda (p.
         ej. "No pudimos subir tu foto de perfil a Drive…", ver
         registrarTutor() en apps-script.gs): antes esto se descartaba con
         un catch sin variable y SIEMPRE mostraba el mensaje genérico de
         abajo, así que aunque el servidor ya avisara del motivo puntual,
         quien llenó el formulario nunca lo veía. */
      setError([err?.message
        || "No pudimos enviar su registro. Revise su conexión y pruebe de nuevo; "
          + "sus datos siguen acá."]);
      setEnviando(false);
      setProgreso(0);
    }
  };

  const otro = () => {
    fotoTokenRef.current++;
    compTokensRef.current = {};
    setNombre(""); setCorreo(""); setCarrera(""); setAnio("");
    setCodigoTel(CODIGO_PAIS_PREDETERMINADO); setTelefono("");
    setCuentaTipo(""); setCuentaNumero(""); setCuentaTitular("");
    setUsuario(""); setPassword(""); setConfirmarPassword("");
    setCategorias([]); setCursosSel([]); setNotas({}); setPrecios({}); setDescs({}); setFoto(null);
    setErrorFoto(""); setErrorComp({});
    setComprobantes({}); setPreparandoComp({});
    setDisp({}); setListo(false); setIntento(false);
  };

  /* Mismo reinicio que "Registrar a alguien más", pero además sube la
     página hasta arriba del todo, igual que el logo de "Ayudate". */
  const volverInicio = () => {
    otro();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (listo) return (
    <div className="form ancho">
      <div className="done">
        <b>¡Solicitud enviada con éxito!</b> Le hemos enviado las instrucciones a su correo
        institucional para verificar sus calificaciones. Por favor, revise su bandeja de entrada
        (y su carpeta de spam o correo no deseado por precaución). En cuanto un administrador
        verifique su comprobante, su tarjeta se publicará en el sitio y le llegará un correo de
        confirmación. ¡Gracias por querer formar parte de Ayudate!
        <button type="button" className="btn btn-p" style={{ width: "100%", marginTop: 14 }}
          onClick={volverInicio}>
          Volver al inicio
        </button>
        <button type="button" className="btn" style={{ width: "100%", marginTop: 8 }} onClick={otro}>
          Registrar a alguien más
        </button>
      </div>
    </div>
  );

  return (
    <div className="form ancho" ref={cajaRef}>
      {aviso && <AvisoPoliticas onAceptar={confirmar} onCerrar={() => setAviso(false)} />}
      <h3>Quiero dar tutorías</h3>
      <p className="d">
        Cuente qué da y cuándo puede. <b>Un administrador revisa su perfil</b> antes de publicarlo.
      </p>

      <div className="notas">
        <p><b>Cómo son las sesiones:</b> sus tutorías son 100% virtuales, por videollamada en{" "}
          la <b>plataforma de reuniones de Ayudate</b>, directo desde el navegador y sin instalar
          nada. Cuando se confirma una sesión, el enlace le llega por correo; la sala se habilita
          automáticamente <b>5 minutos antes</b> del inicio y se cierra <b>3 minutos después</b> de
          que termina.</p>
      </div>

      <label className="lb">¿Dónde quiere dar tutorías?</label>
      <div className={intento && falta.categoria ? "falta-bloque" : ""}>
        <div className="instituciones">
          {INSTITUCIONES.map((inst) => (
            <button type="button" key={inst.id} className="icard"
              aria-pressed={categorias.includes(inst.id)} style={{ "--ac": inst.color }}
              onClick={() => alternarCategoria(inst.id)}>
              <span className="bar" />
              <span className="ilbl">{inst.corto || inst.nombre}</span>
              <span className="idesc">{inst.desc}</span>
            </button>
          ))}
        </div>
      </div>
      <Ayuda texto="Puede elegir varias instituciones a la vez: el buscador de abajo junta los cursos de todas."
        {...ay("categoria")} />

      <div className="fcols">
        {/* ---------- Columna izquierda: datos ---------- */}
        <div className="fcol">
          <label className="lb">Foto de perfil <span className="opc">Opcional</span></label>
          <AlertaArchivo mensaje={errorFoto} />
          <div className="fotorow">
            <div className="fotoprev">
              {foto ? <img src={foto} alt="Vista previa de su foto" /> : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="8.6" r="3.9" fill="currentColor" />
                  <path d="M4.4 20.4c0-3.9 3.4-6.5 7.6-6.5s7.6 2.6 7.6 6.5" fill="currentColor" />
                </svg>
              )}
            </div>
            <div className="fotoacc">
              <label className={`btn fotobtn ${preparando ? "ocupado" : ""}`}>
                {preparando ? "Preparando…" : foto ? "Cambiar foto" : "Subir una foto"}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";            // permite volver a elegir la misma
                  if (!f) return;
                  try {
                    validarExtensionImagen(f);
                  } catch (err) {
                    setErrorFoto(err.message);
                    return;
                  }
                  const miToken = ++fotoTokenRef.current;
                  setPreparando(true); setErrorFoto("");
                  try {
                    const lista = await prepararFoto(f);
                    if (fotoTokenRef.current === miToken) setFoto(lista);
                  } catch (err) {
                    if (fotoTokenRef.current === miToken) setErrorFoto(err.message);
                  } finally {
                    if (fotoTokenRef.current === miToken) setPreparando(false);
                  }
                }} />
              </label>
              {foto && (
                <button type="button" className="fotoquitar"
                  onClick={() => { fotoTokenRef.current++; setFoto(null); setErrorFoto(""); }}>
                  Quitar
                </button>
              )}
              <p className="ayuda" style={{ margin: "8px 0 0" }}>
                Una foto suya, clara y de frente. Formatos
                permitidos: <b>{FORMATOS_IMAGEN_TEXTO}</b>.
              </p>
            </div>
          </div>

          <label className="lb">Sus datos</label>
          <input id="nombre" className={`in ${cl("nombre")}`} placeholder="Nombre completo"
            aria-label="Nombre completo" aria-invalid={intento && falta.nombre}
            value={nombre} onChange={(e) => setNombre(e.target.value.replace(/-/g, ""))} />
          <Ayuda texto="Nombre y apellido, como aparece en su expediente." {...ay("nombre")} />

          <input id="correo" className={`in ${cl("correo")}`} placeholder="Correo"
            aria-label="Correo" aria-invalid={intento && falta.correo}
            type="email" value={correo} onChange={(e) => setCorreo(e.target.value)} />
          <Ayuda texto="Use su correo institucional o personal." {...ay("correo")} />
          <NotaCorreo />

          <div className="lineas">
            <div>
              <input id="carrera" className={`in ${cl("carrera")}`} placeholder="Carrera" aria-label="Carrera"
                aria-invalid={intento && falta.carrera}
                value={carrera} onChange={(e) => setCarrera(e.target.value)} />
              <Ayuda texto="La carrera que estudia. Ej: Ingeniería Mecatrónica." {...ay("carrera")} />
            </div>
            <div>
              <select id="anio" className={`in ${cl("anio")}`} aria-label="Año en curso"
                aria-invalid={intento && falta.anio}
                value={anio} onChange={(e) => setAnio(e.target.value)}>
                <option value="">Año en curso</option>
                {ANIOS_TUTOR.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <Ayuda texto="El año que cursa actualmente." {...ay("anio")} />
            </div>
          </div>

          <TelefonoInput id="telefono" className={cl("telefono")} codigo={codigoTel}
            onCodigoChange={setCodigoTel} valor={telefono} onValorChange={setTelefono}
            ariaInvalid={intento && falta.telefono} placeholder="Teléfono o WhatsApp" />
          <Ayuda texto="Solo dígitos, sin espacios ni guiones."
            {...ay("telefono")} />

          <div className="cuentab">
            <label className="lb">Cuenta para sus pagos</label>
            <p className="ayuda" style={{ margin: "0 0 4px" }}>
              Sus ganancias se depositan ahí directamente cada semana.
            </p>

            <select id="cuenta-tipo" className={`in ${cl("cuentaTipo")}`} aria-label="Tipo de cuenta"
              aria-invalid={intento && falta.cuentaTipo}
              value={cuentaTipo} onChange={(e) => setCuentaTipo(e.target.value)}>
              <option value="">Elija el tipo de cuenta</option>
              {TIPOS_CUENTA.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
            <Ayuda texto="A cuál banco le depositamos." {...ay("cuentaTipo")} />

            <input id="cuenta-numero" className={`in ${cl("cuentaNumero")}`}
              placeholder="IBAN o SINPE Móvil" aria-label="IBAN o SINPE Móvil"
              aria-invalid={intento && falta.cuentaNumero}
              value={cuentaNumero} onChange={(e) => setCuentaNumero(e.target.value)} />
            <Ayuda texto="A cuál número de cuenta IBAN o SINPE Móvil le depositamos."
              {...ay("cuentaNumero")} />

            <input id="cuenta-titular" className={`in ${cl("cuentaTitular")}`} placeholder="A nombre de"
              aria-label="A nombre de" aria-invalid={intento && falta.cuentaTitular}
              value={cuentaTitular} onChange={(e) => setCuentaTitular(e.target.value)} />
            <Ayuda texto="El nombre completo de quien es dueño/a de la cuenta"
              {...ay("cuentaTitular")} />
          </div>

          <label className="lb">Credenciales</label>
          <p className="ayuda" style={{ margin: "0 0 4px" }}>
            <button type="button" className="enlace-login" onClick={() => irA_ruta("/login")}>
              Inicie sesión
            </button>{" "}si ya tiene cuenta.
          </p>
          {disponibilidadUsuario && (
            <div style={{ marginBottom: 6 }}>
              <DisponibilidadUsuario estado={disponibilidadUsuario} />
            </div>
          )}
          <input id="usuario" className={`in ${cl("usuario")}`} placeholder="Usuario"
            aria-label="Usuario" aria-invalid={intento && falta.usuario} autoComplete="username"
            value={usuario} onChange={(e) => setUsuario(e.target.value)} />
          <Ayuda texto="Con este usuario entra después a su dashboard de tutor." {...ay("usuario")} />

          <CampoPassword id="password" className={`in ${cl("password")}`}
            placeholder="Contraseña" aria-label="Contraseña" aria-invalid={intento && falta.password}
            autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <Ayuda texto="Mínimo 8 caracteres." {...ay("password")} />

          <CampoPassword id="confirmar-password" className={`in ${cl("confirmarPassword")}`}
            placeholder="Repita la contraseña" aria-label="Repita la contraseña"
            aria-invalid={intento && falta.confirmarPassword} autoComplete="new-password"
            value={confirmarPassword} onChange={(e) => setConfirmarPassword(e.target.value)} />
          <Ayuda texto="Escríbala de nuevo para confirmar." {...ay("confirmarPassword")} />

          <label className="lb">Qué puede impartir</label>
          <BuscadorCursos key={categorias.join(",")} elegidos={cursosSel} onChange={setCursosSel}
            areas={areasCategoria} deshabilitado={categorias.length === 0}
            marcar={intento && falta.cursos}
            ph={categorias.length === 1 && categorias[0] === "idiomas"
              ? "Escriba el idioma o nivel. Ej: inglés, hebreo"
              : categorias.length === 1 && categorias[0] === "software"
              ? "Escriba la herramienta o el nivel. Ej: excel, python, solidworks"
              : "Escriba el nombre o el código. Ej: mate, cálculo, física"} />
          <Ayuda texto="Cursos, idiomas y software juntos. Cada uno se publica como una oferta aparte, con su propio precio."
            {...ay("cursos")} />

          {cursosSel.length > 0 && (
            <>
              <label className="lb">
                Detalle de cada oferta
              </label>
              <div className="cursosf">
                {cursosSel.map((c) => {
                  const i = infoCurso(c);
                  const malN = (intento && sinNota.includes(c)) || notaFueraDeRango(c);
                  const malP = intento && sinPrecio.includes(c);
                  const malC = intento && sinComprobante.includes(c);
                  const pr = precioDe(precios[c]);
                  return (
                    <div className={`cursof ${malN || malP || malC ? "falta-bloque" : ""}`} key={c}>
                      <button type="button" className="cursof-x" aria-label={`Quitar ${i.nom}`}
                        onClick={() => quitarCurso(c)}>×</button>
                      <span className="cursoft" style={{ "--ac": i.color }}>
                        {!i.sinCodigo && <b>{i.cod}</b>}
                        <em>{i.nom}</em>
                      </span>
                      <div className={`cursofc ${pideRespaldoDe(c) ? "" : "una"}`}>
                        {pideRespaldoDe(c) && (
                        <label>
                          <span>{escalaDe(c).etiqueta}</span>
                          {pideNivel(c) ? (
                            <select className="in" aria-label={`${escalaDe(c).etiqueta} en ${i.nom}`}
                              value={notas[c] ?? ""}
                              onChange={(e) => setNotas({ ...notas, [c]: e.target.value })}>
                              <option value="">Elija</option>
                              {escalaDe(c).niveles.map((n) => (
                                <option key={n.id} value={n.id}>{n.id} · {n.d}</option>
                              ))}
                            </select>
                          ) : (
                            /* Texto con teclado numérico, no type="number": el
                               campo filtra lo que no sea dígito antes de
                               guardarlo (ver soloDigitos), y con type="number"
                               un "12e" llega vacío al onChange y no queda nada
                               que limpiar ni que mostrarle a la persona. */
                            <input type="text" inputMode="numeric" autoComplete="off"
                              className={`in ${notaFueraDeRango(c) ? "falta" : ""}`}
                              placeholder={`${escalaDe(c).min}–${escalaDe(c).max}`}
                              aria-label={`${escalaDe(c).etiqueta} en ${i.nom}`}
                              aria-invalid={notaFueraDeRango(c)}
                              value={notas[c] ?? ""}
                              onChange={(e) => setNotas({ ...notas, [c]: soloDigitos(e.target.value, escalaDe(c)) })} />
                          )}
                          {notaFueraDeRango(c) && (
                            <p className="ayuda mal" role="alert" style={{ margin: "2px 0 0" }}>
                              {escalaDe(c).fuera}
                            </p>
                          )}
                        </label>
                        )}
                        <label>
                          <span>Cobra por hora</span>
                          <input type="number" min={TARIFA_MINIMA} step="100"
                            className={`in ${malP || precioBajo(c) ? "falta" : ""}`}
                            placeholder={`₡${TARIFA_SUGERIDA}`} aria-label={`Precio de ${i.nom}`}
                            aria-invalid={malP || precioBajo(c)}
                            value={precios[c] ?? ""}
                            onChange={(e) => setPrecios({ ...precios, [c]: e.target.value })} />
                          {precioBajo(c) && (
                            <p className="ayuda mal" role="alert" style={{ margin: "2px 0 0" }}>
                              El mínimo son ₡3000
                            </p>
                          )}
                        </label>
                      </div>
                      {Number(precios[c]) >= TARIFA_MINIMA && (
                        <p className="cursofp">
                          El estudiante paga <b>{colones(pr.cliente)}</b> · usted recibe{" "}
                          <b>{colones(pr.tutor)}</b>
                        </p>
                      )}
                      {pideRespaldoDe(c) && (
                      <div className="cursofd">
                        <span>Comprobante de {escalaDe(c).deQue} en {i.nom}</span>
                        <AlertaArchivo mensaje={errorComp[c]} />
                        <div className="comprow">
                          {comprobantes[c] && (
                            <div className="compprev">
                              <img src={comprobantes[c]} alt={`Comprobante de ${i.nom}`} />
                            </div>
                          )}
                          <label className={`btn fotobtn ${preparandoComp[c] ? "ocupado" : ""}`}>
                            {preparandoComp[c] ? "Preparando…" : comprobantes[c] ? "Cambiar" : "Subir comprobante"}
                            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={async (e) => {
                              const f = e.target.files?.[0];
                              e.target.value = "";
                              if (!f) return;
                              try {
                                validarExtensionImagen(f);
                              } catch (err) {
                                setErrorComp((p) => ({ ...p, [c]: err.message }));
                                return;
                              }
                              const miToken = (compTokensRef.current[c] = (compTokensRef.current[c] || 0) + 1);
                              setPreparandoComp((p) => ({ ...p, [c]: true }));
                              setErrorComp((p) => ({ ...p, [c]: "" }));
                              try {
                                const lista = await prepararComprobante(f);
                                if (compTokensRef.current[c] === miToken) {
                                  setComprobantes((prev) => ({ ...prev, [c]: lista }));
                                }
                              } catch (err) {
                                if (compTokensRef.current[c] === miToken) {
                                  setErrorComp((p) => ({ ...p, [c]: err.message }));
                                }
                              } finally {
                                if (compTokensRef.current[c] === miToken) {
                                  setPreparandoComp((p) => ({ ...p, [c]: false }));
                                }
                              }
                            }} />
                          </label>
                          {comprobantes[c] && (
                            <button type="button" className="fotoquitar"
                              onClick={() => {
                                compTokensRef.current[c] = (compTokensRef.current[c] || 0) + 1;
                                setComprobantes((prev) => {
                                  const copia = { ...prev }; delete copia[c]; return copia;
                                });
                                setErrorComp((p) => ({ ...p, [c]: "" }));
                              }}>
                              Quitar
                            </button>
                          )}
                        </div>
                        <i>
                          {escalaDe(c).comprobante}
                          {" "}Formatos permitidos: {FORMATOS_IMAGEN_TEXTO}.
                        </i>
                      </div>
                      )}
                      {!pideRespaldoDe(c) && (
                        <p className="cursofp sinreq">
                          Para materias de colegio <b>no pedimos nota ni comprobante</b>. Un
                          administrador revisa su perfil antes de publicarlo, igual que el resto.
                        </p>
                      )}
                      <label className="cursofd">
                        <span>Qué cubre en {i.nom}</span>
                        <textarea className="in ta" rows={2} maxLength={180}
                          aria-label={`Descripción de ${i.nom}`}
                          placeholder="Ej: integrales por partes y series, con parciales viejos."
                          value={descs[c] ?? ""}
                          onChange={(e) => setDescs({ ...descs, [c]: e.target.value })} />
                        <i className={(descs[c] || "").trim().length < 20 ? "corto" : ""}>
                          {(descs[c] || "").trim().length < 20
                            ? `${(descs[c] || "").trim().length} de 20 mínimo`
                            : `${(descs[c] || "").length}/180`}
                        </i>
                      </label>
                    </div>
                  );
                })}
              </div>
              <Ayuda texto="En cursos de universidad, nota mínima de 80. En idiomas, su nivel de B2 a C2. En software, su nivel de conocimiento. En admisión, la nota que sacó en el examen. En colegio no pedimos nota."
                {...ay("notas")} />
              <Ayuda texto="Suba el comprobante de cada curso que lo pida: lo revisa un administrador antes de publicar su perfil. Las materias de colegio no llevan comprobante."
                {...ay("comprobantes")} />
              <Ayuda texto={`Puede cobrar distinto en cada curso. Mínimo ${colones(TARIFA_MINIMA)}. Al estudiante se le suma un 10% y a usted se le descuenta un 10%.`}
                {...ay("tarifa")} />
            </>
          )}

          <div className="privado">
            <b>Sus notas no se publicarán.</b> Los comprobantes que nos envíe se usan únicamente para
            verificar que cumple el requisito. Ni la nota ni el comprobante aparecen en su perfil ni
            los ve ningún estudiante.
          </div>
        </div>

        {/* ---------- Columna derecha: disponibilidad ---------- */}
        <div className="fcol">
          <div className="displb">
            <label className="lb" style={{ margin: 0 }}>
              Disponibilidad · {marcados} bloque{marcados === 1 ? "" : "s"} marcado{marcados === 1 ? "" : "s"}
            </label>
            {marcados > 0 && (
              <button type="button" className="limpiar" onClick={() => setDisp({})}>
                Limpiar todo
              </button>
            )}
          </div>
          <div className="notas">
            <p>Toque un bloque para <b>marcarlo</b> disponible; tóquelo de nuevo para dejarlo en <b>no disponible</b>.</p>
            <p>Lo que marque vale para <b>todo el semestre</b>. Un administrador le confirma día y hora de cada sesión sobre esa base.</p>
            <p><b>Las tutorías son exclusivamente virtuales.</b> Las reuniones son creadas y gestionadas
              por la administración de Ayudate, que le hará llegar directamente el enlace de acceso
              para conectarse a la videollamada de cada sesión.</p>
          </div>
          <div className={intento && falta.disponibilidad ? "falta-bloque" : ""}>
            <Grilla valor={disp} onChange={setDisp} />
          </div>
          <Ayuda texto="Marque al menos un bloque. Puede marcar todos los que quiera."
            {...ay("disponibilidad")} />
        </div>
      </div>

      {(pendientes.length > 0 || error) && (
        <ListaErrores error={error || pendientes} vivo={!error} />
      )}
      <button className="btn btn-p" style={{ width: "100%", marginTop: 16 }}
        onClick={enviar} disabled={enviando} aria-busy={enviando}>
        {enviando ? "Enviando…" : "Apuntarme como tutor"}
      </button>
      {enviando && (
        <>
          <div className="carga" role="progressbar" aria-label="Enviando su registro de tutor"
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progreso)}>
            <div className="carga-fill" style={{ width: `${progreso}%` }} />
          </div>
          <span className="carga-nota">Subiendo sus datos y comprobantes, no cierre esta ventana…</span>
        </>
      )}
      <p className="oblig">Todos los campos son obligatorios.</p>
    </div>
  );
}
